import { useEffect, useState } from "react";
import { CheckCircle2, CircleHelp, XCircle, ExternalLink, MessageSquare, Phone, Sparkles, BadgeCheck } from "lucide-react";
import { BUSINESS } from "@/lib/site";
import { trackClick } from "@/lib/api";
import { PROVIDER_DISCLAIMER } from "./DentistChecker";

const STATUS = {
  in_network: { label: "In network", Icon: CheckCircle2, tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  not_found: { label: "Not found", Icon: XCircle, tone: "bg-slate-50 text-slate-600 border-slate-200" },
  needs_verification: { label: "Needs verification", Icon: CircleHelp, tone: "bg-amber-50 text-amber-800 border-amber-200" },
};

const fmtVerified = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const VerifiedBadge = ({ m, className = "" }) => (
  <span
    data-testid="dentist-verified-badge"
    className={`inline-flex items-center gap-1.5 rounded-full bg-[var(--ink)] text-white text-[11px] font-semibold px-3 py-1 ${className}`}
  >
    <BadgeCheck className="w-3.5 h-3.5 text-[var(--brand-secondary)]" />
    Confirmed by {m.verified_by || "Christian"} on {fmtVerified(m.verified_at)}
  </span>
);

const dentistLabel = (m) => {
  const name = [m.first_name, m.last_name].filter(Boolean).join(" ");
  return name ? `Dr. ${name}` : m.practice;
};

const buildMessage = (m, query, result) => {
  const who = m ? `${dentistLabel(m)}${m.practice && m.last_name ? ` at ${m.practice}` : ""} (${m.city} ${m.zip_code})` : `Dr. ${query.first_name ? `${query.first_name} ` : ""}${query.last_name}${query.practice ? `, ${query.practice}` : ""} near ${query.location}`;
  const checked = result.carriers.map((c) => c.name).join(", ");
  const found = m ? m.results.map((r) => `${r.carrier_name}: ${STATUS[r.status].label}`).join("; ") : "no match in the local list";
  return `Hi Christian — can you confirm whether ${who} accepts any of these dental plans? Carriers checked: ${checked}. Tool result: ${found}.`;
};

const VerifyLink = ({ carrier, className }) => (
  <a
    href={carrier.finder_url}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`dentist-verify-${carrier.id}`}
    onClick={() => trackClick(`dentist-verify-${carrier.id}`, `Verify on ${carrier.name}`, "/dental")}
    className={className}
  >
    Verify on {carrier.name} <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
  </a>
);

const AskButton = ({ onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="cta-kinetic inline-flex items-center justify-center rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white text-sm font-medium px-5 h-11"
  >
    <MessageSquare className="w-4 h-4 mr-2" /> Ask Christian to confirm
  </button>
);

export const DentistResults = ({ result, query, onAskChristian }) => {
  const { matches, carriers } = result;
  const [selected, setSelected] = useState(0);
  useEffect(() => setSelected(0), [result]);

  if (matches.length === 0) {
    return (
      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="dentist-results-empty">
        <p className="eyebrow">No match yet</p>
        <h3 className="mt-2 font-display text-xl text-[var(--ink)]">
          We didn’t find “{query.last_name}” near {result.search_zip || query.location} in our local list.
        </h3>
        <p className="mt-3 text-sm text-slate-700 leading-relaxed">
          I can still check this by hand. Text or call Christian at{" "}
          <a href={`tel:${BUSINESS.phoneRaw}`} className="font-semibold text-[var(--brand-primary)]" data-testid="dentist-empty-phone">
            {BUSINESS.phone}
          </a>
          , or send the details below and I’ll confirm with each carrier.
        </p>
        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <AskButton testid="dentist-ask-christian" onClick={() => onAskChristian?.(buildMessage(null, query, result))} />
          <a
            href={`tel:${BUSINESS.phoneRaw}`}
            className="inline-flex items-center justify-center rounded-full border border-slate-300 text-[var(--ink)] hover:border-[var(--brand-primary)] text-sm font-medium px-5 h-11 transition-colors"
          >
            <Phone className="w-4 h-4 mr-2" /> Call {BUSINESS.phone}
          </a>
        </div>
        <p className="mt-6 text-[11px] uppercase tracking-[0.14em] text-slate-500">Or search the official directories</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {carriers.map((c) => (
            <VerifyLink key={c.id} carrier={c} className="inline-flex items-center rounded-full border border-slate-200 bg-white hover:border-[var(--brand-primary)] text-xs font-medium text-slate-700 px-3.5 h-9 transition-colors" />
          ))}
        </div>
      </div>
    );
  }

  const m = matches[Math.min(selected, matches.length - 1)];
  const inNetwork = m.results.filter((r) => r.status === "in_network");
  const bestFit = inNetwork.length >= 2;

  return (
    <div className="mt-5" data-testid="dentist-results">
      {matches.length > 1 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="dentist-match-picker">
          <p className="text-sm text-[var(--ink)] font-medium">
            We found {matches.length} possible matches — pick yours:
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {matches.map((x, i) => (
              <button
                key={x.id}
                type="button"
                data-testid={`dentist-match-${i}`}
                aria-pressed={i === selected}
                onClick={() => setSelected(i)}
                className={`rounded-full px-4 h-9 text-xs font-medium border transition-colors ${
                  i === selected
                    ? "bg-[var(--brand-primary)] border-[var(--brand-primary)] text-white"
                    : "bg-white border-slate-200 text-slate-600 hover:border-[var(--brand-primary)]"
                }`}
              >
                {dentistLabel(x)}{x.last_name && x.practice ? ` · ${x.practice}` : ""} · {x.city}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="dentist-selected">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <p className="eyebrow">Results for</p>
            <h3 className="mt-2 font-display text-2xl text-[var(--ink)]" data-testid="dentist-selected-name">{dentistLabel(m)}</h3>
            <p className="mt-1 text-sm text-slate-600">
              {m.last_name && m.practice ? `${m.practice} · ` : ""}{m.city}, {m.state} {m.zip_code}
              {m.distance_miles != null && ` · ${m.distance_miles} mi from ${result.search_zip}`}
            </p>
            {m.verified_at && <VerifiedBadge m={m} className="mt-3" />}
          </div>
          <AskButton testid="dentist-ask-christian" onClick={() => onAskChristian?.(buildMessage(m, query, result))} />
        </div>

        {bestFit && (
          <div className="mt-6 flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-900" data-testid="dentist-best-fit">
            <Sparkles className="w-4 h-4 mt-0.5 flex-none" />
            <span>
              <strong>Best fit to keep this dentist:</strong> {inNetwork.map((r) => r.carrier_name).join(", ")}.
            </span>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {m.results.map((r) => {
            const meta = STATUS[r.status];
            const carrier = carriers.find((c) => c.id === r.carrier_id);
            const highlight = bestFit && r.status === "in_network";
            return (
              <div
                key={r.carrier_id}
                data-testid={`dentist-result-${r.carrier_id}`}
                data-status={r.status}
                className={`rounded-xl border p-5 flex flex-col ${highlight ? "border-emerald-300 ring-2 ring-emerald-200/60" : "border-slate-200"}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="font-display text-base text-[var(--ink)]">{r.carrier_name}</p>
                  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${meta.tone}`}>
                    <meta.Icon className="w-3.5 h-3.5" /> {meta.label}
                  </span>
                </div>
                <p className="mt-2 text-xs text-slate-500 leading-relaxed flex-1">
                  {r.status === "in_network" && m.verified_at && `Confirmed by ${m.verified_by || "Christian"} on ${fmtVerified(m.verified_at)}. Still confirm with the office before enrolling.`}
                  {r.status === "in_network" && !m.verified_at && "Listed as participating in our local table. Confirm with the office before enrolling."}
                  {r.status === "not_found" && "Not listed as participating in our local table. The official directory has the final word."}
                  {r.status === "needs_verification" && "We haven’t confirmed this one yet — check the official finder or ask Christian."}
                </p>
                <VerifyLink
                  carrier={carrier}
                  className="mt-4 inline-flex items-center justify-center rounded-full border border-slate-300 text-[var(--ink)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] text-xs font-medium px-4 h-9 transition-colors"
                />
              </div>
            );
          })}
        </div>
        <p className="mt-6 text-[11px] leading-relaxed text-slate-500" data-testid="dentist-results-disclaimer">
          {PROVIDER_DISCLAIMER}
        </p>
      </div>
    </div>
  );
};
